<script setup lang="ts">
import { nextTick, useTemplateRef } from "vue";

import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useContextMenuStore } from "@/store/contextMenu";
import { boundsCoord, clientCoord } from "@/utils/dom/coordinates";
import { isKeyDown } from "@/utils/shortcut/shortcuts";

import type { BoardContextMenuMode } from "./BoardContextMenu.vue";
import BoardContextMenu from "./BoardContextMenu.vue";

const props = defineProps<{ mode: BoardContextMenuMode; tooltip?: string }>();

const buttonRef = useTemplateRef("button");

function showMenu(e: MouseEvent) {
  const fromKeyboard = isKeyDown("Enter") && !!buttonRef.value;
  const position = fromKeyboard ? boundsCoord(buttonRef.value) : clientCoord(e);

  // Open the context menu where the user clicked
  useContextMenuStore().open(
    BoardContextMenu,
    { position, mode: props.mode },
    { close: onKebabClose(fromKeyboard) },
  );
  return;
}

/**
 * When the kebab menu is closed, return the focus to the trigger
 * (but then blur it to hide the focus ring, unless it was opened by keyboard)
 * If we don't do this, the tab index moves to the start of the page because of how
 * the context menu is rendered
 */
function onKebabClose(openedByKeyboard: boolean) {
  return async () => {
    await nextTick();
    buttonRef.value?.focus();

    if (!openedByKeyboard) {
      await nextTick();
      buttonRef.value?.blur();
    }
  };
}
</script>

<template>
  <IconButton ref="button" icon="system/more-horizontal" :text="tooltip" @click="showMenu" />
</template>
